import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { GridApi, GridOptions } from 'ag-grid-community';
import { isEqual } from 'lodash-es';

import { TableViewAdministrationState } from '../common/enum/table-view-administration-state.enum';
import { TableViewCustomModel } from '../tva-custom/common/model/table-view-custom-model';
import { TvaCustomService } from '../tva-custom/tva-custom.service';

import { TableViewSessionModel } from './common/table-view-session-model';
import { TvaSessionStoreService } from './common/tva-session-store.service';

@Injectable({
  providedIn: 'root',
})

/**
 * Additional infomrations: see Confluence (Table View Administration (Session))
 * Link: https://skyway.porsche.com/confluence/x/MiSwEQ
 */
export class TvaSessionService {
  private readonly router = inject(Router);
  private readonly tvaSessionStoreService = inject(TvaSessionStoreService);

  public isTableViewFromTableViewSessionAvailable = false;

  public tableViewAreaEnumVal: string; // TODO: fix modifiers
  public isTableWithPivotFunctionality = false;
  public gridOptions: GridOptions;
  public gridApi: GridApi;
  public tvaCustomService: TvaCustomService;

  /**
   *
   * @param tableViewAreaEnumVal            - Location identifier of tableView
   * @param isTableWithPivotFunctionality   - RootComponent is using a PivotTable like reportingComponent
   * @param gridOptions                     - GridOptions-Object
   * @param tableViewAdministrationService  - Reference to tvaCustomService
   * @param gridApi                         - GridApi-Object
   *
   * Reference to tvaCustomService:
   * When RootComponent embedded
   */
  public init(
    tableViewAreaEnumVal: string,
    isTableWithPivotFunctionality: boolean,
    gridOptions: GridOptions,
    tableViewAdministrationService: TvaCustomService,
    gridApi: GridApi,
  ): void {
    this.tableViewAreaEnumVal = tableViewAreaEnumVal;
    this.isTableWithPivotFunctionality = isTableWithPivotFunctionality;
    this.gridOptions = gridOptions;
    this.gridApi = gridApi;
    this.tvaCustomService = tableViewAdministrationService;

    this.gridApi.addEventListener('gridPreDestroyed', this.onGridPreDestroyed);

    this.doSetTableViewFromTableViewSession(tableViewAreaEnumVal);
  }

  // Event-Handler for gridPreDestroyed
  private readonly onGridPreDestroyed = (): void => {
    switch (this.tableViewAreaEnumVal) {
      case TableViewAdministrationState.BookingOverview: {
        this.tvaSessionStoreService.addTableViewSessionToMapTableViewSession(
          TableViewAdministrationState.BookingOverview,
          this.getTableViewSessionObj(),
        );
        break;
      }
      case TableViewAdministrationState.CampaignOverview: {
        this.tvaSessionStoreService.addTableViewSessionToMapTableViewSession(
          TableViewAdministrationState.CampaignOverview,
          this.getTableViewSessionObj(),
        );
        break;
      }
      case TableViewAdministrationState.Approval: {
        this.tvaSessionStoreService.addTableViewSessionToMapTableViewSession(
          TableViewAdministrationState.Approval,
          this.getTableViewSessionObj(),
        );
        break;
      }
      case TableViewAdministrationState.AfterSales: {
        this.tvaSessionStoreService.addTableViewSessionToMapTableViewSession(
          TableViewAdministrationState.AfterSales,
          this.getTableViewSessionObj(),
        );
        break;
      }
      case TableViewAdministrationState.PartsAgreementsOverview: {
        this.tvaSessionStoreService.addTableViewSessionToMapTableViewSession(
          TableViewAdministrationState.PartsAgreementsOverview,
          this.getTableViewSessionObj(),
        );
        break;
      }
      default: {
        break;
      }
    }
  };
  /**
   * Checks whether a sessionTableView is stored in the sessionStore.
   * If so, the values of the current tableView are overwritten with the values from the sessionTableView.
   */
  private doSetTableViewFromTableViewSession(tableViewAreaEnumVal: string): void {
    const tableViewSessionModel: TableViewSessionModel =
      this.tvaSessionStoreService.getTableViewSessionFromMapTableViewSessionByKey(tableViewAreaEnumVal);

    try {
      if (tableViewSessionModel) {
        this.isTableViewFromTableViewSessionAvailable = true;
        this.gridApi.updateGridOptions({ pivotMode: tableViewSessionModel.pivotMode });
        this.gridApi.setFilterModel(JSON.parse(tableViewSessionModel.filterModel));
        this.gridApi.applyColumnState({
          state: JSON.parse(tableViewSessionModel.columnState),
          applyOrder: true,
        });

        this.doCheckForAdaptionsOnTableViewAdministrationComp(tableViewSessionModel);
      } else {
        // tableViewSessionModel has no entry for this page while opening page the first time
        this.gridApi.sizeColumnsToFit();
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(`Something went wrong: ${error}`);
    }
  }

  /**
   * If the tableViewAdministration component is used in addition to the tableViewAdministrationSession component,
   * the last states (when leaving the page) must also be restored there.
   */
  private doCheckForAdaptionsOnTableViewAdministrationComp(tableViewSessionModel: TableViewSessionModel): void {
    if (this.tvaCustomService) {
      this.tvaCustomService.setSelectedTableViewOnlyOnSelectionComponent(
        tableViewSessionModel.tableViewCustomModel,
        this.isSessionModelEqualCustomModel(tableViewSessionModel),
      );
    }
  }

  private isSessionModelEqualCustomModel(tableViewSessionModel: TableViewSessionModel): boolean {
    const customModel = tableViewSessionModel.tableViewCustomModel;
    if (!customModel) {
      return false;
    }
    return (
      isEqual(tableViewSessionModel.filterModel, customModel.filterModel) &&
      isEqual(tableViewSessionModel.pivotMode, customModel.pivotMode) &&
      isEqual(tableViewSessionModel.columnState, customModel.columnState)
    );
  }

  private getTableViewSessionObj(): TableViewSessionModel {
    /*
     If the tableViewAdministrationCustom-Component embedded in the rootComponent we need to know, which tableView is selected.
     So on the next access to this page we can set the same tableView which was selected when we leaf this page.
     */
    let tableViewModel: TableViewCustomModel = null;
    if (this.tvaCustomService) {
      tableViewModel =
        this.tvaCustomService.getSelectedTableView() == null
          ? this.tvaCustomService.CONST_SYSTEM_DEFAULT_TABLE_VIEW
          : this.tvaCustomService.getSelectedTableView();
    }

    try {
      return new TableViewSessionModel(
        this.isTableWithPivotFunctionality === true ? this.gridApi.isPivotMode() : false,
        JSON.stringify(this.gridApi.getColumnState()),
        JSON.stringify(this.gridApi.getFilterModel()),
        tableViewModel,
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(`Something went wrong: ${error}`);
    }
  }
}
